/** Mirrors BerryCookie.Contracts.Banners and .Consents (JSON, camelCase). */
export type BannerLayout = "bar" | "popup" | "corner";

export interface BannerTheme {
  background: string;
  text: string;
  primary: string;
  primaryText: string;
  secondary: string;
  secondaryText: string;
  radius: number;
  fontFamily: string;
  /**
   * "Reject all" and "Accept all" always have the same size, row and font; the style changes only colors.
   * "balanced": both in the primary colors, "Settings" stays secondary.
   * "emphasizeAccept" or missing (banners saved before the setting): "Accept all" in the primary colors,
   * "Reject all" outlined in the banner's text color.
   */
  buttonStyle?: ButtonStyle;
}

export type ButtonStyle = "balanced" | "emphasizeAccept";

export interface BannerBehavior {
  showRejectAll: boolean;
  showSettings: boolean;
  consentLifetimeMonths: number;
  privacyPolicyUrl: string | null;
  showBranding: boolean;
  /** List the declared cookies under each category in the preferences (default true). */
  showCookieList?: boolean;
}

export interface GoogleConsentModeSettings {
  enabled: boolean;
  urlPassthrough: boolean;
  adsDataRedaction: boolean;
}

/** optIn: nothing optional before consent (GDPR); optOut: allowed until refused; none: no banner. */
export type ConsentModeKind = "optIn" | "optOut" | "none";

export interface BannerCategory {
  key: string;
  required: boolean;
  enabled: boolean;
}

export interface CategoryTexts {
  name: string;
  description: string;
}

export interface BannerTexts {
  title: string;
  message: string;
  acceptAll: string;
  rejectAll: string;
  settings: string;
  save: string;
  /** Text of the link to the cookie/privacy policy. */
  privacyPolicy: string;
  /** The policy in this language; falls back to behavior.privacyPolicyUrl. */
  privacyPolicyUrl?: string | null;
  categories: Record<string, CategoryTexts>;
}

export interface BannerSettings {
  layout: BannerLayout;
  position: string;
  theme: BannerTheme;
  behavior: BannerBehavior;
  categories: BannerCategory[];
  defaultLanguage: string;
  texts: Record<string, BannerTexts>;
  googleConsentMode?: GoogleConsentModeSettings | null;
  tcf?: TcfSettings | null;
}

/** IAB TCF on the banner: Global Vendor List ids to disclose (empty: all). */
export interface TcfSettings {
  enabled: boolean;
  vendors: number[];
  publisherCountry?: string | null;
}

/** Sent by the Consent API when the banner runs the TCF (and the plan includes it). */
export interface PublicTcf {
  cmpId: number;
  cmpVersion: number;
  publisherCountry: string | null;
  vendors: number[];
}

/** A declared cookie, shown in the preferences under its category. */
export interface PublicCookie {
  name: string;
  category: string;
  provider: string | null;
  purpose: string | null;
  expirySeconds: number | null;
  kind: "Cookie" | "LocalStorage" | "SessionStorage";
  /** The built-in purpose in the banner's other languages; absent for a purpose the owner wrote. */
  purposes?: Record<string, string> | null;
}

export interface PublicBannerConfig {
  version: number;
  settings: BannerSettings;
  /** The visitor's country as the Consent API sees it, or null. */
  country: string | null;
  /** How consent works for this visitor (from the banner's regional rules). */
  mode: ConsentModeKind;
  cookies: PublicCookie[];
  tcf?: PublicTcf | null;
  /** White-label (Agency plan): "Powered by {name}" linking to url instead of BerryCookie's credit line. */
  branding?: PublicBranding | null;
}

export interface PublicBranding {
  name: string;
  url: string;
}

/** "GPC": not chosen on the banner, applied from the browser's Global Privacy Control in an opt-out region. */
export type ConsentAction = "AcceptAll" | "RejectAll" | "Custom" | "GPC";

/** The category Global Privacy Control refuses (selling/sharing for advertising). */
export const GPC_REFUSED = "marketing";

export const NECESSARY = "necessary";
